/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Button, Typography, TextField, Chip, Autocomplete,
  CircularProgress, Alert, Divider, IconButton, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptIcon from '@mui/icons-material/Receipt';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import { api } from '../../../api';

const TEAL = '#00798E';
const fmt = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const STEP_FORM = 'FORM';
const STEP_REVIEW = 'REVIEW';
const STEP_DONE = 'DONE';

// ─── Sub-components ────────────────────────────────────────────────────────────

function CnSummaryBanner({ cn }) {
  return (
    <Box sx={{
      p: 1.5, mb: 2, borderRadius: 2,
      background: 'linear-gradient(135deg, #f3e5f5 0%, #ede7f6 100%)',
      border: '1px solid #ce93d8',
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#6a1b9a', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Credit Note
          </Typography>
          <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: '#4a148c', fontFamily: 'monospace' }}>
            {cn.credit_note_no}
          </Typography>
          <Typography sx={{ fontSize: '0.7rem', color: '#888', mt: 0.25 }}>
            {fmtDate(cn.credit_note_date)}
            {cn.reason ? ` · ${cn.reason}` : ''}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography sx={{ fontSize: '0.68rem', color: '#9c27b0', textTransform: 'uppercase' }}>Balance</Typography>
          <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, color: '#6a1b9a', fontFamily: 'monospace' }}>
            {fmt(cn.balance_amount)}
          </Typography>
          {cn.amount !== cn.balance_amount && (
            <Typography sx={{ fontSize: '0.65rem', color: '#aaa' }}>of {fmt(cn.amount)}</Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}

function AdjTypeToggle({ value, onChange, disabled }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.75 }}>
        Adjustment Method *
      </Typography>
      <ToggleButtonGroup
        value={value}
        exclusive
        onChange={(_, v) => { if (v) onChange(v); }}
        disabled={disabled}
        size="small"
        sx={{ width: '100%', '& .MuiToggleButton-root': { flex: 1, fontSize: '0.75rem', fontWeight: 600, py: 0.9, textTransform: 'none', border: '1px solid #e0e0e0', color: '#555' }, '& .Mui-selected': { background: `${TEAL}15 !important`, color: `${TEAL} !important`, borderColor: `${TEAL} !important` } }}
      >
        <ToggleButton value="INVOICE">Adjust Against Invoice</ToggleButton>
        <ToggleButton value="CASH">Cash Refund</ToggleButton>
        <ToggleButton value="ONLINE">Online / UPI</ToggleButton>
      </ToggleButtonGroup>
      <Typography sx={{ fontSize: '0.67rem', color: '#888', mt: 0.5 }}>
        {value === 'INVOICE' && 'Deduct this credit note from an existing open invoice.'}
        {value === 'CASH' && 'Refund the credit note amount in cash.'}
        {value === 'ONLINE' && 'Refund via UPI / bank transfer — UTR number required.'}
      </Typography>
    </Box>
  );
}

// ─── Main Dialog ───────────────────────────────────────────────────────────────

export default function DebitNoteDialog({ open, creditNote, contactId, onClose, onSuccess }) {
  const [step, setStep] = useState(STEP_FORM);
  const [adjType, setAdjType] = useState('INVOICE');
  const [invoiceOptions, setInvoiceOptions] = useState([]);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [utr, setUtr] = useState('');
  const [bankName, setBankName] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState('');
  const [createdDn, setCreatedDn] = useState(null);

  // Reset when dialog opens/closes or CN changes
  useEffect(() => {
    if (open) {
      setStep(STEP_FORM);
      setAdjType('INVOICE');
      setSelectedInvoice(null);
      setUtr('');
      setBankName('');
      setReason('');
      setError('');
      setCreatedDn(null);
    }
  }, [open, creditNote?.id]);

  // Load open invoices for this contact when INVOICE mode selected
  const loadInvoices = useCallback(() => {
    if (!contactId) return;
    setInvoiceLoading(true);
    api.pharmacy.listBills({ contact_id: contactId, status: 'FINALIZED', limit: 50 })
      .then(res => {
        const bills = res?.rows || res?.data || (Array.isArray(res) ? res : []);
        // Only show bills that still have a balance (balance_amount > 0)
        setInvoiceOptions(bills.filter(b => Number(b.balance_amount || 0) > 0));
      })
      .catch(() => setInvoiceOptions([]))
      .finally(() => setInvoiceLoading(false));
  }, [contactId]);

  useEffect(() => {
    if (open && adjType === 'INVOICE') loadInvoices();
  }, [open, adjType, loadInvoices]);

  // Validation
  const validate = () => {
    if (adjType === 'ONLINE' && !utr.trim()) return 'UTR / transaction reference is required for online payment.';
    if (adjType === 'INVOICE' && !selectedInvoice) return 'Please select an invoice to adjust against.';
    return '';
  };

  // Step 1 — Create DRAFT debit note
  const handleCreate = async () => {
    const errMsg = validate();
    if (errMsg) { setError(errMsg); return; }
    setError('');
    setSaving(true);
    try {
      const payload = {
        irm_pharm_bill_credit_notes_id: creditNote.id,
        adjustment_type: adjType,
        ...(adjType === 'INVOICE' && { irm_pharm_bills_id: selectedInvoice.id }),
        ...(adjType === 'ONLINE' && { payment_reference: utr.trim(), bank_name: bankName.trim() || undefined }),
        ...(adjType === 'CASH' && {}),
        reason: reason.trim() || undefined,
      };
      const dn = await api.pharmacy.createDebitNote(payload);
      setCreatedDn(dn);
      setStep(STEP_REVIEW);
    } catch (e) {
      setError(e?.message || e?.data?.message || 'Failed to create debit note.');
    } finally {
      setSaving(false);
    }
  };

  // Step 2 — Apply the DRAFT
  const handleApply = async () => {
    if (!createdDn) return;
    setApplying(true);
    setError('');
    try {
      await api.pharmacy.applyDebitNote(createdDn.id);
      setStep(STEP_DONE);
      onSuccess?.();
    } catch (e) {
      setError(e?.message || e?.data?.message || 'Failed to apply debit note.');
    } finally {
      setApplying(false);
    }
  };

  // Discard draft and close
  const handleDiscardAndClose = async () => {
    if (createdDn && step === STEP_REVIEW) {
      try { await api.pharmacy.cancelDebitNote(createdDn.id, { cancel_reason: 'Discarded' }); } catch { /* ignore */ }
    }
    onClose();
  };

  if (!creditNote) return null;

  return (
    <Dialog
      open={open}
      onClose={step === STEP_DONE ? onClose : handleDiscardAndClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
    >
      {/* Header */}
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{
          background: 'linear-gradient(135deg, #4a148c 0%, #7b1fa2 100%)',
          px: 2.5, py: 1.75,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MoneyOffIcon sx={{ color: '#fff', fontSize: 20 }} />
            <Box>
              <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '0.95rem', lineHeight: 1.2 }}>
                Create Debit Note
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.7rem' }}>
                Reverse / settle credit note {creditNote.credit_note_no}
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={step === STEP_DONE ? onClose : handleDiscardAndClose} sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: '#fff' } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Progress dots */}
        <Box sx={{ display: 'flex', gap: 0.5, px: 2.5, py: 1, background: '#fafafa', borderBottom: '1px solid #eee' }}>
          {[STEP_FORM, STEP_REVIEW, STEP_DONE].map((s, i) => (
            <Box key={s} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{
                width: 22, height: 22, borderRadius: '50%',
                background: step === s ? TEAL : (step === STEP_DONE || (step === STEP_REVIEW && s === STEP_FORM) || (step === STEP_DONE && s !== STEP_DONE)) ? `${TEAL}40` : '#e0e0e0',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#fff' }}>{i + 1}</Typography>
              </Box>
              <Typography sx={{ fontSize: '0.65rem', color: step === s ? TEAL : '#aaa', fontWeight: step === s ? 700 : 400 }}>
                {s === STEP_FORM ? 'Configure' : s === STEP_REVIEW ? 'Review' : 'Done'}
              </Typography>
              {i < 2 && <Box sx={{ width: 20, height: 1, background: '#e0e0e0', mx: 0.25 }} />}
            </Box>
          ))}
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 2.5, py: 2, minHeight: 320 }}
        onKeyDown={(e) => {
          if (e.key !== 'Enter' || e.shiftKey || saving || applying) return;
          const tag = (e.target.tagName || '').toLowerCase();
          if (tag === 'textarea') return;
          if (e.target.closest && e.target.closest('[aria-expanded="true"]')) return;
          if (step === STEP_FORM && (e.ctrlKey || e.metaKey)) { e.preventDefault(); handleCreate(); }
          else if (step === STEP_REVIEW) { e.preventDefault(); handleApply(); }
        }}>

        {/* ── STEP 1: Form ── */}
        {step === STEP_FORM && (
          <>
            <CnSummaryBanner cn={creditNote} />

            <AdjTypeToggle value={adjType} onChange={v => { setAdjType(v); setSelectedInvoice(null); setError(''); }} disabled={saving} />

            {adjType === 'INVOICE' && (
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.75 }}>
                  Select Invoice *
                </Typography>
                <Autocomplete
                  options={invoiceOptions}
                  loading={invoiceLoading}
                  getOptionLabel={opt => `${opt.bill_no} · ${fmtDate(opt.bill_date)} · Bal: ${fmt(opt.balance_amount)}`}
                  value={selectedInvoice}
                  onChange={(_, v) => setSelectedInvoice(v)}
                  size="small"
                  renderInput={params => (
                    <TextField {...params} placeholder="Search open invoices…"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.82rem' } }}
                      InputProps={{ ...params.InputProps, startAdornment: <ReceiptIcon sx={{ fontSize: 16, color: '#aaa', mr: 0.5 }} />, endAdornment: invoiceLoading ? <CircularProgress size={14} /> : params.InputProps.endAdornment }}
                    />
                  )}
                  renderOption={(props, opt) => (
                    <Box component="li" {...props} sx={{ py: 0.75, px: 1.5, fontSize: '0.8rem' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                        <Box>
                          <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, fontFamily: 'monospace' }}>{opt.bill_no}</Typography>
                          <Typography sx={{ fontSize: '0.68rem', color: '#888' }}>{fmtDate(opt.bill_date)} · {opt.patient_name}</Typography>
                        </Box>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: TEAL, fontFamily: 'monospace' }}>{fmt(opt.balance_amount)}</Typography>
                      </Box>
                    </Box>
                  )}
                  noOptionsText={invoiceLoading ? 'Loading…' : 'No open invoices found'}
                />
                {selectedInvoice && (
                  <Box sx={{ mt: 1, p: 1, borderRadius: 1.5, background: '#e8f5e9', border: '1px solid #a5d6a7' }}>
                    <Typography sx={{ fontSize: '0.72rem', color: '#2e7d32', fontWeight: 600 }}>
                      {selectedInvoice.bill_no} · Balance {fmt(selectedInvoice.balance_amount)}
                      {Number(creditNote.balance_amount) > Number(selectedInvoice.balance_amount) && (
                        <Typography component="span" sx={{ fontSize: '0.68rem', color: '#e65100', ml: 1 }}>
                          (CN exceeds invoice balance — partial adjustment)
                        </Typography>
                      )}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {adjType === 'ONLINE' && (
              <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <TextField
                  size="small" label="UTR / Transaction Reference *"
                  value={utr} onChange={e => setUtr(e.target.value)}
                  placeholder="e.g. 412601234567"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.82rem' } }}
                />
                <TextField
                  size="small" label="Bank / Wallet Name (optional)"
                  value={bankName} onChange={e => setBankName(e.target.value)}
                  placeholder="e.g. HDFC, PhonePe, GPay"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.82rem' } }}
                />
              </Box>
            )}

            {adjType === 'CASH' && (
              <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, background: '#e8f5e9', border: '1px solid #a5d6a7' }}>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#2e7d32' }}>
                  Cash refund of {fmt(creditNote.balance_amount)} will be recorded.
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#555', mt: 0.25 }}>
                  A journal entry (DR Cash Refund · CR Credit Notes Payable) will be posted automatically.
                </Typography>
              </Box>
            )}

            <TextField
              size="small" multiline minRows={2} label="Reason / Note (optional)"
              value={reason} onChange={e => setReason(e.target.value)}
              fullWidth
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.82rem' } }}
            />

            {error && <Alert severity="error" sx={{ mt: 1.5, fontSize: '0.78rem', borderRadius: 2, py: 0.5 }}>{error}</Alert>}
          </>
        )}

        {/* ── STEP 2: Review ── */}
        {step === STEP_REVIEW && createdDn && (
          <>
            <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, background: '#fff8e1', border: '1px solid #ffe082' }}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#f57f17', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.25 }}>
                Review before applying
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: '#555' }}>
                Once applied, the credit note will be marked as USED and the journal entry will be posted. This cannot be undone.
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {[
                ['Debit Note No.', createdDn.debit_note_no, '#4a148c'],
                ['Credit Note', creditNote.credit_note_no, '#6a1b9a'],
                ['Amount', fmt(creditNote.balance_amount), TEAL],
                ['Adjustment Type', createdDn.adjustment_type, '#333'],
                ...(createdDn.adjustment_type === 'INVOICE' ? [['Invoice', selectedInvoice?.bill_no || '—', '#333']] : []),
                ...(createdDn.adjustment_type === 'ONLINE' ? [['UTR', utr, '#333'], ...(bankName ? [['Bank', bankName, '#333']] : [])] : []),
                ...(reason ? [['Reason', reason, '#888']] : []),
              ].map(([label, value, color]) => (
                <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.6, px: 1.2, borderRadius: 1.5, background: '#fafafa', border: '1px solid #eee' }}>
                  <Typography sx={{ fontSize: '0.72rem', color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</Typography>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color, fontFamily: label === 'Amount' || label.includes('No') ? 'monospace' : 'inherit' }}>{value}</Typography>
                </Box>
              ))}
            </Box>

            {error && <Alert severity="error" sx={{ mt: 1.5, fontSize: '0.78rem', borderRadius: 2, py: 0.5 }}>{error}</Alert>}
          </>
        )}

        {/* ── STEP 3: Done ── */}
        {step === STEP_DONE && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4, gap: 1.5 }}>
            <Box sx={{ width: 56, height: 56, borderRadius: '50%', background: '#e8f5e9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <MoneyOffIcon sx={{ fontSize: 28, color: '#2e7d32' }} />
            </Box>
            <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: '#2e7d32' }}>Debit Note Applied</Typography>
            <Typography sx={{ fontSize: '0.78rem', color: '#666', textAlign: 'center', maxWidth: 320 }}>
              {createdDn?.debit_note_no} has been applied. The credit note has been marked as USED and the journal entry has been posted.
            </Typography>
            <Chip label={createdDn?.debit_note_no} sx={{ fontFamily: 'monospace', fontWeight: 700, background: '#e8f5e9', color: '#2e7d32', fontSize: '0.78rem', mt: 0.5 }} />
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 2.5, py: 1.5, gap: 1 }}>
        {step === STEP_FORM && (
          <>
            <Button size="small" onClick={onClose} disabled={saving}
              sx={{ color: '#888', fontSize: '0.78rem', textTransform: 'none' }}>
              Cancel
            </Button>
            <Button size="small" variant="contained" onClick={handleCreate} disabled={saving}
              sx={{
                background: 'linear-gradient(135deg, #4a148c 0%, #7b1fa2 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #6a1b9a 0%, #9c27b0 100%)' },
                color: '#fff !important', fontWeight: 700, fontSize: '0.82rem',
                textTransform: 'none', borderRadius: 2, px: 2.5,
                '&.Mui-disabled': { background: '#b0bec5 !important' },
              }}>
              {saving ? <CircularProgress size={14} sx={{ color: '#fff', mr: 0.75 }} /> : null}
              Next: Review
            </Button>
          </>
        )}

        {step === STEP_REVIEW && (
          <>
            <Button size="small" onClick={handleDiscardAndClose} disabled={applying}
              sx={{ color: '#888', fontSize: '0.78rem', textTransform: 'none' }}>
              Discard
            </Button>
            <Button size="small" onClick={() => setStep(STEP_FORM)} disabled={applying}
              sx={{ color: TEAL, fontSize: '0.78rem', textTransform: 'none' }}>
              ← Back
            </Button>
            <Button size="small" variant="contained" onClick={handleApply} disabled={applying}
              sx={{
                background: 'linear-gradient(135deg, #1b5e20 0%, #388e3c 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #2e7d32 0%, #43a047 100%)', boxShadow: '0 4px 14px rgba(46,125,50,0.4)' },
                color: '#fff !important', fontWeight: 700, fontSize: '0.82rem',
                textTransform: 'none', borderRadius: 2, px: 2.5,
                '&.Mui-disabled': { background: '#b0bec5 !important' },
              }}>
              {applying ? <CircularProgress size={14} sx={{ color: '#fff', mr: 0.75 }} /> : null}
              Apply Debit Note
            </Button>
          </>
        )}

        {step === STEP_DONE && (
          <Button size="small" variant="contained" onClick={onClose}
            sx={{ background: TEAL, '&:hover': { background: '#005f70' }, color: '#fff !important', fontWeight: 700, fontSize: '0.82rem', textTransform: 'none', borderRadius: 2, px: 2.5 }}>
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
